import React, { useState, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import Modal from 'components/molecules/modal';
import {
  ModalContent,
  Label,
  ButtonContainer,
  SimpleTable,
  ResultsContainer,
  LabelIconWrapper,
  LoadingContainer,
  LinkageTableInput,
} from './../link-thirdparty-modal/LinkThirdpartyModal.styles';
import { useConfig } from 'config';
import { useAuth } from 'auth';
import { Button, Loading, Tooltip, Typography } from 'components/atoms';
import { useFetch } from 'hooks';
import { ThirdPartyBasicData } from '../types';
import LabelIcon from 'components/molecules/label-icon';
import { Icon } from 'components/atoms';
import { ReactComponent as UnknownTypeIcon } from 'assets/svg/Icons/Unknown.svg';
import { LinkedThirdParty } from 'pages/initiate-due-diligence/types';
import { LinkedThirdPartyFieldsConfigs, ThirdPartyType } from 'types';
import PercentageInput from 'components/atoms/percentage-input';
import { useTerminologyConfig } from 'context/DataContext';

type EditLinkageThirdpartyModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  onLinked?: (linkedThirdParty: LinkedThirdParty) => void;
  title: string;
  mainThirdparty: ThirdPartyBasicData;
  linkedThirdParty: ThirdPartyBasicData;
  linkedThirdPartyConfigurations: LinkedThirdPartyFieldsConfigs;
};

const EditLinkageThirdpartyModal: React.FC<EditLinkageThirdpartyModalProps> = ({
  isOpen,
  onClose,
  onLinked,
  title,
  mainThirdparty,
  linkedThirdParty,
  linkedThirdPartyConfigurations,
}) => {
  const { vantageWebApi } = useConfig();
  const terminologyConfig = useTerminologyConfig();
  const { tokenInfo } = useAuth();
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    trigger: fetchThirdPartyLinkageTrigger,
    loading: thirdPartyLinkageLoading,
    data: thirdPartyLinkageData,
  } = useFetch(
    `${vantageWebApi}/thirdParty/${mainThirdparty.thirdpartyId}/link/${linkedThirdParty.thirdpartyId}/edit`,
    {
      method: 'GET',
      lazy: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  const { trigger: editThirdPartyLinkageTrigger, loading: editThirdPartyLinkageLoading } = useFetch(
    `${vantageWebApi}/thirdParty/${mainThirdparty.thirdpartyId}/link/${linkedThirdParty.thirdpartyId}/edit`,
    {
      method: 'PUT',
      lazy: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  function getSchema(selectedType: string, config: LinkedThirdPartyFieldsConfigs) {
    let requireRelation = false;
    let requirePercent = false;

    switch (selectedType) {
      case 'Individual':
        requireRelation = config?.isIndividualRelationshipTypeMandatory;
        requirePercent =
          config?.isIndividualOwnershipPercentageMandatory &&
          config?.isIndividualOwnershipPercentageEnabled;
        break;

      case 'Business':
        requireRelation = config?.isCompanyRelationshipTypeMandatory;
        requirePercent =
          config?.isCompanyOwnershipPercentageMandatory &&
          config?.isCompanyOwnershipPercentageEnabled;
        break;

      case 'Unknown':
        requireRelation = config?.isUnknownRelationshipTypeMandatory;
        requirePercent =
          config?.isUnknownOwnershipPercentageMandatory &&
          config?.isUnknownOwnershipPercentageEnabled;
        break;
    }

    return yup.object({
      relationship1to2: yup
        .string()
        .nullable()
        .when([], {
          is: () => requireRelation,
          then: (schema) => schema.required(),
        }),
      relationship2to1: yup
        .string()
        .nullable()
        .when([], {
          is: () => requireRelation,
          then: (schema) => schema.required(),
        }),
      percentage: yup
        .number()
        .nullable()
        .when([], {
          is: () => requirePercent,
          then: (schema) => schema.required().min(0).max(100),
        }),
    });
  }

  const selectedType = linkedThirdParty.type;

  function getConfigValue(config, type, key) {
    if (type === 'Business') return config?.[`isCompany${key}`];
    if (type === 'Individual') return config?.[`isIndividual${key}`];
    if (type === 'Unknown') return config?.[`isUnknown${key}`];
    return false;
  }

  const isPercentageEnabled = getConfigValue(
    linkedThirdPartyConfigurations,
    selectedType,
    'OwnershipPercentageEnabled'
  );
  const isRelationshipRequired = getConfigValue(
    linkedThirdPartyConfigurations,
    selectedType,
    'RelationshipTypeMandatory'
  );
  const isPercentageRequired = getConfigValue(
    linkedThirdPartyConfigurations,
    selectedType,
    'OwnershipPercentageMandatory'
  );

  const schema = useMemo(
    () => getSchema(selectedType, linkedThirdPartyConfigurations),
    [selectedType, linkedThirdPartyConfigurations]
  );

  const {
    control: controlLinkThirdparty,
    reset: resetLinkThirdparty,
    formState: { isValid },
    handleSubmit: handleLinkThirdpartySubmit,
    setValue: setLinkageFormValue,
  } = useForm({
    defaultValues: {
      relationship1to2: null,
      relationship2to1: null,
      percentage: null,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const isProceedDisabled = !isValid || thirdPartyLinkageLoading || editThirdPartyLinkageLoading;

  useEffect(() => {
    if (isOpen) {
      fetchThirdPartyLinkageTrigger();
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    if (thirdPartyLinkageData) {
      setLinkageFormValue('relationship1to2', thirdPartyLinkageData.relationship2to1 ?? null);
      setLinkageFormValue('relationship2to1', thirdPartyLinkageData.relationship1to2 ?? null);
      setLinkageFormValue('percentage', thirdPartyLinkageData.percentage ?? null);
    }
  }, [thirdPartyLinkageData, setLinkageFormValue]);

  const handleOnCloseModal = () => {
    resetLinkThirdparty();
    if (onClose) {
      onClose();
    }
  };

  const onSubmit = async (values: any) => {
    try {
      const response = await editThirdPartyLinkageTrigger({
        body: JSON.stringify(values),
      });
      if (onLinked && response?.linkedThirdParty2) {
        onLinked(response.linkedThirdParty2);
      }
    } finally {
      handleOnCloseModal();
    }
  };

  const getThirdPartyIcon = (type: string) => {
    switch (type) {
      case ThirdPartyType.Individual:
        return <Icon icon={'user'} aria-hidden size="xxxxs" />;
      case ThirdPartyType.Business:
        return <Icon icon={'company'} aria-hidden size="xxxxs" />;
      default:
        return <UnknownTypeIcon aria-hidden />;
    }
  };

  function getRelationshipPlaceholder(mainType: string, selectedType: string) {
    if (mainType === 'Business' && selectedType === 'Individual') return 'e.g. Business';
    if (mainType === 'Individual' && selectedType === 'Business') return 'e.g. Shareholder';
    if (mainType === 'Individual' && selectedType === 'Individual') return 'e.g. Relative';
    if (mainType === 'Business' && selectedType === 'Business') return 'e.g. Subsidiary';
    return 'e.g. Relationship';
  }

  const showLinkageTable = !thirdPartyLinkageLoading && thirdPartyLinkageData;

  const handleRelationshipChange = (value: string, onChange: (v: string) => void) => {
    if (value.trim() === '' && value.length > 0) return;
    onChange(value);
  };

  return (
    <Modal
      isOpen={isOpen}
      customOverlayWidth="700px"
      onClose={() => handleOnCloseModal()}
      title={title}
    >
      <ModalContent>
        {thirdPartyLinkageLoading && (
          <LoadingContainer>
            <Loading />
          </LoadingContainer>
        )}
        {showLinkageTable && (
          <ResultsContainer>
            <Typography value="Relationship details" className="table-section-title" tag="h3" />
            <SimpleTable>
              <thead>
                <tr>
                  <th>{terminologyConfig?.thirdPartySingularNaming ?? 'Third party'}</th>
                  <th>
                    {'Relationship'}
                    {isRelationshipRequired && <span style={{ color: 'red' }}> *</span>}
                  </th>
                  {isPercentageEnabled && (
                    <th>
                      {'Percentage'}
                      {isPercentageRequired && <span style={{ color: 'red' }}> *</span>}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <LabelIconWrapper>
                      <LabelIcon
                        label={linkedThirdParty.name}
                        icon={getThirdPartyIcon(linkedThirdParty.type)}
                      />
                      <Label>to</Label>
                      <LabelIcon
                        label={mainThirdparty.name}
                        icon={getThirdPartyIcon(mainThirdparty.type)}
                      />
                    </LabelIconWrapper>
                  </td>
                  <td>
                    <Controller
                      name="relationship2to1"
                      control={controlLinkThirdparty}
                      render={({ field }) => (
                        <LinkageTableInput
                          value={field.value || ''}
                          id="relationship2to1"
                          placeholder={getRelationshipPlaceholder(
                            linkedThirdParty.type,
                            mainThirdparty.type
                          )}
                          onChange={(e) => handleRelationshipChange(e.target.value, field.onChange)}
                        />
                      )}
                    />
                  </td>
                  {isPercentageEnabled && (
                    <td>
                      <Controller
                        name="percentage"
                        control={controlLinkThirdparty}
                        render={({ field }) => (
                          <PercentageInput
                            value={field.value || ''}
                            placeholder="0%"
                            onValueChange={field.onChange}
                            input={LinkageTableInput}
                          />
                        )}
                      />
                    </td>
                  )}
                </tr>
                <tr>
                  <td>
                    <LabelIconWrapper>
                      <LabelIcon
                        label={mainThirdparty.name}
                        icon={getThirdPartyIcon(mainThirdparty.type)}
                      />
                      <Label>to</Label>
                      <LabelIcon
                        label={linkedThirdParty.name}
                        icon={getThirdPartyIcon(linkedThirdParty.type)}
                      />
                    </LabelIconWrapper>
                  </td>
                  <td>
                    <Controller
                      name="relationship1to2"
                      control={controlLinkThirdparty}
                      render={({ field }) => (
                        <LinkageTableInput
                          value={field.value || ''}
                          id="relationship1to2"
                          placeholder={getRelationshipPlaceholder(
                            mainThirdparty.type,
                            linkedThirdParty.type
                          )}
                          onChange={(e) => handleRelationshipChange(e.target.value, field.onChange)}
                        />
                      )}
                    />
                  </td>
                  {isPercentageEnabled && (
                    <td>
                      <Controller
                        name="percentage"
                        control={controlLinkThirdparty}
                        render={({ field }) => (
                          <PercentageInput
                            value={field.value || ''}
                            placeholder="0%"
                            onValueChange={field.onChange}
                            input={LinkageTableInput}
                          />
                        )}
                      />
                    </td>
                  )}
                </tr>
              </tbody>
            </SimpleTable>
          </ResultsContainer>
        )}
      </ModalContent>
      <ButtonContainer>
        <Button text className="button" onClick={() => handleOnCloseModal()}>
          Cancel
        </Button>
        <Tooltip
          tooltipText="Please populate all mandatory fields in the above table."
          placement="top"
          tooltipShown={showTooltip && isProceedDisabled}
          bodyStyles="padding: 10px; padding-bottom: 10px;"
        >
          <span
            style={{ display: 'inline-block' }}
            onMouseEnter={() => isProceedDisabled && setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Button
              secondary
              className="button"
              onClick={handleLinkThirdpartySubmit(onSubmit)}
              disabled={isProceedDisabled}
            >
              {editThirdPartyLinkageLoading ? <Loading /> : 'Proceed'}
            </Button>
          </span>
        </Tooltip>
      </ButtonContainer>
    </Modal>
  );
};

export default EditLinkageThirdpartyModal;
